import { ChangeEvent, useState } from 'react';
import { NON_DIGIT_CHARS } from '@access-cms/shared-constants';

import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { Drawer } from '../../../../components/Drawer';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { required, requiredChoice, zipCode } from '../../utils/fieldValidation';
import { STATE_AND_TERRITORY_OPTIONS } from '../../../../utils/states';
import './AddressInformationPage.css';

export interface AddressInformationFormData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  workFromHome: boolean | null;
}

const workFromHomeOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function AddressInformationPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<AddressInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'address-information-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'addressLine1', label: 'Personal Street Address Line 1', validationRules: [required] },
      { field: 'city', label: 'Personal Address City', validationRules: [required] },
      { field: 'state', label: 'Personal Address State', validationRules: [required] },
      { field: 'zipCode', label: 'Personal Address Zip Code', validationRules: [required, zipCode] },
      { field: 'workFromHome', label: 'Do you work from home?', validationRules: [requiredChoice] },
    ],
  });

  const handleChange = (field: keyof AddressInformationFormData, value: string | boolean | null) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handleZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const digitsOnly = input.replace(NON_DIGIT_CHARS, '');

    // Allow up to 9 digits for ZIP+4 format
    const limitedDigits = digitsOnly.slice(0, 9);

    // Format as 12345-6789 if more than 5 digits
    let formattedZip = limitedDigits;
    if (limitedDigits.length > 5) {
      formattedZip = `${limitedDigits.slice(0, 5)}-${limitedDigits.slice(5)}`;
    }

    updateData({
      ...data,
      zipCode: formattedZip,
    });
    handleValidationChange('zipCode', formattedZip);
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-address-line-1`}>
        <TextField
          required
          autoComplete='address-line1'
          data-testid={`${baseTestId}-address-line-1-field`}
          errorMessage={getFieldError('addressLine1')}
          label='Personal Street Address Line 1'
          name='addressLine1'
          value={data.addressLine1 || ''}
          onBlur={() => handleBlur('addressLine1')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('addressLine1', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-address-line-2`}>
        <TextField
          autoComplete='address-line2'
          data-testid={`${baseTestId}-address-line-2-field`}
          errorMessage={fieldErrors?.addressLine2}
          label='Personal Street Address Line 2'
          name='addressLine2'
          value={data.addressLine2 || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('addressLine2', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-city`}>
        <TextField
          required
          autoComplete='address-level2'
          data-testid={`${baseTestId}-city-field`}
          errorMessage={getFieldError('city')}
          label='Personal Address City'
          name='city'
          value={data.city || ''}
          onBlur={() => handleBlur('city')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('city', e.target.value)}
        />
      </div>

      <div
        className='address-info-row ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-state-zip`}>
        <div data-testid={`${baseTestId}-state`}>
          <Dropdown
            required
            searchable
            autoComplete='address-level1'
            data-testid={`${baseTestId}-state-dropdown`}
            errorMessage={getFieldError('state')}
            hint='Select your state or territory'
            label='Personal Address State/Territory'
            name='state'
            options={STATE_AND_TERRITORY_OPTIONS}
            value={data.state || ''}
            onBlur={() => handleBlur('state')}
            onChange={(value: string) => handleChange('state', value)}
          />
        </div>

        <div data-testid={`${baseTestId}-zip`}>
          <TextField
            required
            autoComplete='postal-code'
            data-testid={`${baseTestId}-zip-field`}
            errorMessage={getFieldError('zipCode')}
            label='Personal Address Zip Code'
            name='zipCode'
            value={data.zipCode || ''}
            onBlur={() => handleBlur('zipCode')}
            onChange={handleZipCodeChange}
          />
        </div>
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-work-from-home`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-work-from-home-dropdown`}
          errorMessage={getFieldError('workFromHome')}
          hint='Select whether you work from home'
          label='Do you work from home?'
          name='workFromHome'
          options={workFromHomeOptions}
          tooltipText={`If you answer 'Yes' to confirm you work from home, then your work address will automatically be populated with your home address.`}
          value={data.workFromHome ?? null}
          onBlur={() => handleBlur('workFromHome')}
          onChange={(value: boolean | null) => handleChange('workFromHome', value)}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Address Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help for Personal Address Details</h3>
          <p>Enter your personal address. This should be the location where you primarily live.</p>

          <ul>
            <li>
              <strong>Address Line 1:</strong> Enter your home street address.
            </li>
            <li>
              <strong>Address Line 2:</strong> Enter your home's suite, floor, or building number (if this applies).
            </li>
            <li>
              <strong>City:</strong> Enter the city name where your home is.
            </li>
            <li>
              <strong>State:</strong> Select your home's state from the dropdown.
            </li>
            <li>
              <strong>Zip Code:</strong> Enter your home's 5-digit ZIP code.
            </li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Do you work from home?</h4>
          <p>
            Let us know if you work from your home address. This helps us understand how you will work. It may also
            affect certain benefits or requirements.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default AddressInformationPage;
